/**
 * Maps screens and modals to user documentation entries.
 * Used by the inline help button to show context-specific docs.
 *
 * Route patterns are matched in order; first match wins.
 * Use exact paths for specificity, or prefix patterns for families/:familyId routes.
 */

export type HelpContextKey =
  | 'families'
  | 'create-family'
  | 'add-pet'
  | 'pet-profile'
  | 'upload'
  | 'family-upload'
  | 'family-overview'
  | 'family-tree'
  | 'family-members'
  | 'add-family-member'
  | 'person-profile'
  | 'person-profile-edit'
  | 'person-profile-memories'
  | 'family-media'
  | 'family-memories'
  | 'memory-detail'
  | 'family-sources'
  | 'family-activity'
  | 'family-review'
  | 'family-suggestions'
  | 'invite'
  | 'profile'
  | 'delete-account'
  | 'help'
  | 'changelog'
  | 'claim-profile-modal'
  | 'edit-family-name-modal';

/** Maps route path patterns to help context keys. More specific patterns must come first. */
const ROUTE_TO_CONTEXT: Array<{ pattern: RegExp | string; context: HelpContextKey }> = [
  { pattern: '/create-family', context: 'create-family' },
  { pattern: /^\/upload$/, context: 'upload' },
  { pattern: /^\/profile\/delete-account/, context: 'delete-account' },
  { pattern: /^\/profile/, context: 'profile' },
  { pattern: /^\/help/, context: 'help' },
  { pattern: /^\/changelog/, context: 'changelog' },
  { pattern: /\/families\/[^/]+\/pets\/add$/, context: 'add-pet' },
  { pattern: /\/families\/[^/]+\/pets\/[^/]+/, context: 'pet-profile' },
  { pattern: /\/families\/[^/]+\/people\/add$/, context: 'add-family-member' },
  { pattern: /\/families\/[^/]+\/people\/[^/]+/, context: 'person-profile' },
  { pattern: /\/families\/[^/]+\/upload/, context: 'family-upload' },
  { pattern: /\/families\/[^/]+\/overview$/, context: 'family-overview' },
  { pattern: /\/families\/[^/]+\/hybrid-pedigree/, context: 'family-tree' },
  { pattern: /\/families\/[^/]+\/members$/, context: 'family-members' },
  { pattern: /\/families\/[^/]+\/memories\/[^/]+/, context: 'memory-detail' },
  { pattern: /\/families\/[^/]+\/memories/, context: 'family-memories' },
  { pattern: /\/families\/[^/]+\/media/, context: 'family-media' },
  { pattern: /\/families\/[^/]+\/sources/, context: 'family-sources' },
  { pattern: /\/families\/[^/]+\/activity$/, context: 'family-activity' },
  { pattern: /\/families\/[^/]+\/review$/, context: 'family-review' },
  { pattern: /\/families\/[^/]+\/suggestions$/, context: 'family-suggestions' },
  { pattern: /\/families\/[^/]+\/invite/, context: 'invite' },
  { pattern: '/families', context: 'families' }
];

/** Maps help context to doc section ID */
export const CONTEXT_TO_DOC_ID: Record<HelpContextKey, string> = {
  families: 'getting-started',
  'create-family': 'creating-family',
  upload: 'importing-data',
  'family-upload': 'importing-data',
  'family-overview': 'family-overview',
  'family-tree': 'viewing-tree',
  'family-members': 'managing-members',
  'add-family-member': 'adding-family-members',
  'add-pet': 'adding-managing-pets',
  'pet-profile': 'adding-managing-pets',
  'person-profile': 'person-profiles',
  'person-profile-edit': 'person-profiles',
  'person-profile-memories': 'viewing-memories',
  'family-media': 'viewing-media',
  'family-memories': 'viewing-memories',
  'memory-detail': 'viewing-memories',
  'family-sources': 'managing-sources',
  'family-activity': 'family-activity',
  'family-review': 'review-queue',
  'family-suggestions': 'review-queue',
  invite: 'inviting-users',
  profile: 'account-management',
  'delete-account': 'delete-account',
  help: 'getting-started',
  changelog: 'getting-started',
  'claim-profile-modal': 'claiming-profile',
  'edit-family-name-modal': 'custom-family-name'
};

/** Resolve help context for current route */
export function getHelpContextForRoute(pathname: string, search?: string): HelpContextKey | null {
  if (search && new URLSearchParams(search).get('edit') === 'true') {
    const personMatch = pathname.match(/\/families\/[^/]+\/people\/[^/]+/);
    if (personMatch) {
      return 'person-profile-edit';
    }
  }

  if (search && new URLSearchParams(search).get('section') === 'memories') {
    const personMatch = pathname.match(/\/families\/[^/]+\/people\/[^/]+/);
    if (personMatch) {
      return 'person-profile-memories';
    }
  }

  for (const { pattern, context } of ROUTE_TO_CONTEXT) {
    const matches =
      typeof pattern === 'string' ? pathname === pattern || pathname.startsWith(pattern + '/') : pattern.test(pathname);
    if (matches) {
      return context;
    }
  }

  return null;
}
